import { useState, useCallback } from 'react';

// Hook para manejar estados de carga y errores
export const useAsyncState = (initialState = null) => {
  const [data, setData] = useState(initialState);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const execute = useCallback(async (asyncFunction, ...args) => {
    try {
      setLoading(true);
      setError(null);
      const result = await asyncFunction(...args);
      setData(result);
      return result;
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const reset = useCallback(() => {
    setData(initialState);
    setLoading(false);
    setError(null);
  }, [initialState]);

  return {
    data,
    loading,
    error,
    execute,
    reset,
    setData,
    setLoading,
    setError
  };
};

// Hook para manejar paginación
export const usePagination = (initialPageSize = 10) => {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [totalCount, setTotalCount] = useState(0);

  const handlePageChange = useCallback((newPage) => {
    setPage(newPage);
  }, []);

  const handlePageSizeChange = useCallback((newPageSize) => {
    setPageSize(newPageSize);
    setPage(0); // Reset to first page when changing page size
  }, []);

  const resetPagination = useCallback(() => {
    setPage(0);
    setPageSize(initialPageSize);
    setTotalCount(0);
  }, [initialPageSize]);

  return {
    page,
    pageSize,
    totalCount,
    setTotalCount,
    handlePageChange,
    handlePageSizeChange,
    resetPagination
  };
};

// Hook para manejar filtros
export const useFilters = (initialFilters = {}) => {
  const [filters, setFilters] = useState(initialFilters);

  const updateFilter = useCallback((key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  }, []);

  const updateFilters = useCallback((newFilters) => {
    setFilters(prev => ({
      ...prev,
      ...newFilters
    }));
  }, []);

  const resetFilters = useCallback(() => {
    setFilters(initialFilters);
  }, [initialFilters]);

  const clearFilter = useCallback((key) => {
    setFilters(prev => {
      const newFilters = { ...prev };
      delete newFilters[key];
      return newFilters;
    });
  }, []);

  return {
    filters,
    updateFilter,
    updateFilters,
    resetFilters,
    clearFilter
  };
};

// Hook para manejar ordenamiento
export const useSorting = (initialSortModel = []) => {
  const [sortModel, setSortModel] = useState(initialSortModel);

  const handleSortModelChange = useCallback((newModel) => {
    setSortModel(newModel);
  }, []);

  const resetSorting = useCallback(() => {
    setSortModel(initialSortModel);
  }, [initialSortModel]);

  return {
    sortModel,
    handleSortModelChange,
    resetSorting
  };
};

// Hook para manejar selección de elementos
export const useSelection = () => {
  const [selectedItems, setSelectedItems] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);

  const handleSelectionChange = useCallback((newSelection) => {
    setSelectedItems(newSelection);
    setSelectedItem(newSelection.length === 1 ? newSelection[0] : null);
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedItems([]);
    setSelectedItem(null);
  }, []);

  const selectItem = useCallback((item) => {
    setSelectedItem(item);
    setSelectedItems([item]);
  }, []);

  return {
    selectedItems,
    selectedItem,
    handleSelectionChange,
    clearSelection,
    selectItem
  };
};
